import { ref } from "vue";
import Unipile from "@/services/unipile/unipile";
import type { Attachment } from "@/dtos/Unipile.dto";

const getCacheKey = (messageId: string, attachmentId: string): string => {
  return `attachment_${messageId}_${attachmentId}`;
};

const getCachedBlob = async (key: string): Promise<Blob | null> => {
  try {
    if ("caches" in window) {
      const cache = await caches.open("attachment-cache-v1");
      const cachedResponse = await cache.match(key);
      if (cachedResponse) {
        return await cachedResponse.blob();
      }
    }
  } catch (error) {
    console.warn("Cache access failed:", error);
  }
  return null;
};

const cacheBlob = async (key: string, blob: Blob): Promise<void> => {
  try {
    if ("caches" in window) {
      const cache = await caches.open("attachment-cache-v1");
      await cache.put(key, new Response(blob));
    }
  } catch (error) {
    console.warn("Cache storage failed:", error);
  }
};

// Prevent network flooding when many video attachments mount simultaneously
const MAX_CONCURRENT_LOADS = 2;
let _activeLoads = 0;
const _waitQueue: Array<() => void> = [];

const acquireSlot = (): Promise<void> => {
  if (_activeLoads < MAX_CONCURRENT_LOADS) {
    _activeLoads++;
    return Promise.resolve();
  }
  return new Promise((resolve) => _waitQueue.push(resolve));
};

const releaseSlot = () => {
  const next = _waitQueue.shift();
  if (next) {
    next();
  } else {
    _activeLoads--;
  }
};

const LOAD_TIMEOUT_MS = 5 * 60_000; // 5 minutes

export const useAttachmentLoader = (
  attachment: Attachment,
  messageId?: string,
  onProgress?: (progress: number) => void
) => {
  const isLoading = ref(false);
  const loadingProgress = ref(0);
  const loadedBytes = ref(0);
  const blobUrl = ref<string | null>(null);
  const error = ref(false);
  let _abortController: AbortController | null = null;
  let _unmounted = false;

  const loadAttachment = async (defaultMimeType: string): Promise<Blob | null> => {
    if (!messageId || !attachment.id) return null;

    if (attachment.url && !attachment.url.startsWith("att://")) {
      return null;
    }

    // Cancel any previous in-flight request before starting a new one
    _abortController?.abort();
    _abortController = new AbortController();
    const { signal } = _abortController;

    await acquireSlot();

    // Component may have unmounted while waiting in the queue
    if (signal.aborted) {
      releaseSlot();
      return null;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      isLoading.value = true;
      loadingProgress.value = 0;
      loadedBytes.value = 0;
      error.value = false;

      const cacheKey = getCacheKey(messageId, attachment.id);
      let blob: Blob | null = await getCachedBlob(cacheKey);

      if (!blob) {
        const _ctrl = _abortController;
        timeoutId = setTimeout(() => _ctrl.abort(), LOAD_TIMEOUT_MS);

        const response = await Unipile.getMessageAttachment(
          messageId,
          attachment.id,
          (progress, loaded) => {
            loadingProgress.value = progress;
            loadedBytes.value = loaded;
            onProgress?.(progress);
          },
          signal
        );

        clearTimeout(timeoutId);
        timeoutId = null;

        const mimeType = response.headers["content-type"] || attachment.mimetype || defaultMimeType;
        blob = new Blob([response.data], { type: mimeType });
        await cacheBlob(cacheKey, blob);
        loadingProgress.value = 100;
      } else {
        loadingProgress.value = 100;
      }

      if (blob) {
        if (!_unmounted) {
          blobUrl.value = window.URL.createObjectURL(blob);
        }
        return blob;
      }

      return null;
    } catch (err: any) {
      // Aborts are intentional (unmount or retry) — don't show error UI
      if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") {
        return null;
      }
      console.error("Failed to load attachment:", err);
      error.value = true;
      return null;
    } finally {
      if (timeoutId !== null) clearTimeout(timeoutId);
      releaseSlot();
      isLoading.value = false;
      setTimeout(() => {
        loadingProgress.value = 0;
      }, 300);
    }
  };

  const getAttachmentUrl = (url: string | undefined): string => {
    if (!url) return "";
    if (url.startsWith("att://")) {
      return blobUrl.value || "";
    }
    return url;
  };

  const cleanup = () => {
    _unmounted = true;
    // Do NOT abort the in-flight request here — let the download complete in the
    // background so the result gets cached. The next time this attachment is
    // displayed it will load instantly from cache instead of re-downloading.
    _abortController = null;
    if (blobUrl.value) {
      window.URL.revokeObjectURL(blobUrl.value);
      blobUrl.value = null;
    }
  };

  return {
    isLoading,
    loadingProgress,
    loadedBytes,
    blobUrl,
    error,
    loadAttachment,
    getAttachmentUrl,
    cleanup,
  };
};
